import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { incidentService } from '../../../services/incidentService';
import { useLanguage } from '../../../contexts/LanguageContext';
import MobilePhotoInput from '../../../components/ui/MobilePhotoInput';

const IncidentForm = ({ sites, selectedSiteId, onSiteChange, onSubmit, companyId }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    incidentType: '',
    incidentDate: new Date()?.toISOString()?.slice(0, 16),
    locationDescription: '',
    description: '',
    severityLevel: '',
    injuredParty: '',
    witnessName: '',
    witnessContact: '',
    immediateActionTaken: '',
    correctiveMeasures: ''
  });

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const incidentTypeOptions = [
    { value: 'near_miss', label: t('nearMiss') },
    { value: 'injury', label: t('injury') },
    { value: 'property_damage', label: t('propertyDamage') },
    { value: 'unsafe_act', label: t('unsafeAct') }
  ];

  const severityOptions = [
    { value: 'low', label: t('low') },
    { value: 'medium', label: t('medium') },
    { value: 'high', label: t('high') },
    { value: 'critical', label: t('critical') }
  ];

  const siteOptions = sites?.map(site => ({
    value: site?.id,
    label: site?.site_name
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e?.target?.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedSiteId) {
      newErrors.siteId = t('selectSiteRequired');
    }
    if (!formData?.incidentType) {
      newErrors.incidentType = t('incidentTypeRequired');
    }
    if (!formData?.incidentDate) {
      newErrors.incidentDate = t('dateTimeRequired');
    }
    if (!formData?.locationDescription?.trim()) {
      newErrors.locationDescription = t('locationDescriptionRequired');
    }
    if (!formData?.description?.trim()) {
      newErrors.description = t('incidentDescriptionRequired');
    }
    if (!formData?.severityLevel) {
      newErrors.severityLevel = t('severityLevelRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setUploading(true);

      let filePaths = [];
      if (files?.length > 0) {
        for (const file of files) {
          const uploadResult = await incidentService?.uploadIncidentFile(file, companyId, selectedSiteId);
          if (uploadResult?.filePath) {
            filePaths?.push(uploadResult?.filePath);
          }
        }
      }

      const result = await onSubmit({
        ...formData,
        filePaths
      });

      if (result?.success) {
        setFormData({
          incidentType: '',
          incidentDate: new Date()?.toISOString()?.slice(0, 16),
          locationDescription: '',
          description: '',
          severityLevel: '',
          injuredParty: '',
          witnessName: '',
          witnessContact: '',
          immediateActionTaken: '',
          correctiveMeasures: ''
        });
        setFiles([]);
        setErrors({});
      } else {
        setErrors({ submit: result?.error || t('failedToSubmitReport') });
      }
    } catch (err) {
      setErrors({ submit: t('errorSubmittingReport') });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg shrink-0">
          <Icon name="AlertTriangle" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('reportNewIncident')}</h2>
          <p className="text-sm text-muted-foreground">{t('reportNewIncidentSubtitle')}</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Site + Incident Type — stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label={t('site')}
            options={siteOptions}
            value={selectedSiteId}
            onChange={onSiteChange}
            error={errors?.siteId}
            required
          />

          <Select
            label={t('incidentType')}
            options={incidentTypeOptions}
            value={formData?.incidentType}
            onChange={(value) => handleChange('incidentType', value)}
            error={errors?.incidentType}
            required
          />
        </div>

        {/* Date + Severity — stacked on mobile, severity full-width button group on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('dateAndTime')}
            type="datetime-local"
            value={formData?.incidentDate}
            onChange={(e) => handleChange('incidentDate', e?.target?.value)}
            error={errors?.incidentDate}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('severityLevel')} <span className="text-destructive">*</span>
            </label>
            {/* Full-width button group on mobile */}
            <div className="flex gap-2 md:hidden">
              {[
                { value: 'low', label: t('low'), color: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
                { value: 'medium', label: t('medium'), color: 'border-amber-300 bg-amber-50 text-amber-700' },
                { value: 'high', label: t('high'), color: 'border-red-300 bg-red-50 text-red-700' },
                { value: 'critical', label: t('critical'), color: 'border-red-500 bg-red-100 text-red-800' },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  type="button"
                  onClick={() => handleChange('severityLevel', opt?.value)}
                  className={`flex-1 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all min-h-[48px] ${
                    formData?.severityLevel === opt?.value ? opt?.color + ' ring-2 ring-offset-1 ring-current' : 'border-border bg-background text-muted-foreground'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
            {/* Select on md+ */}
            <div className="hidden md:block">
              <Select
                options={severityOptions}
                value={formData?.severityLevel}
                onChange={(value) => handleChange('severityLevel', value)}
                error={errors?.severityLevel}
                required
              />
            </div>
            {errors?.severityLevel && <p className="text-sm text-destructive mt-1">{errors?.severityLevel}</p>}
          </div>
        </div>

        <Input
          label={t('locationDescription')}
          type="text"
          placeholder={t('locationDescriptionPlaceholder')}
          value={formData?.locationDescription}
          onChange={(e) => handleChange('locationDescription', e?.target?.value)}
          error={errors?.locationDescription}
          required
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('incidentDescription')} <span className="text-destructive">*</span>
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
            placeholder={t('incidentDescriptionPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
          />
          {errors?.description && (
            <p className="text-sm text-destructive mt-1">{errors?.description}</p>
          )}
        </div>

        <div className="border-t border-border pt-5">
          <h3 className="text-md font-semibold text-foreground mb-4">{t('personnelInvolvement')}</h3>
          <div className="space-y-4">
            <Input
              label={t('injuredParty')}
              type="text"
              placeholder={t('injuredPartyPlaceholder')}
              value={formData?.injuredParty}
              onChange={(e) => handleChange('injuredParty', e?.target?.value)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('witnessName')}
                type="text"
                placeholder={t('witnessNamePlaceholder')}
                value={formData?.witnessName}
                onChange={(e) => handleChange('witnessName', e?.target?.value)}
              />

              <Input
                label={t('witnessContact')}
                type="text"
                placeholder={t('witnessContactPlaceholder')}
                value={formData?.witnessContact}
                onChange={(e) => handleChange('witnessContact', e?.target?.value)}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <h3 className="text-md font-semibold text-foreground mb-4">{t('actionsTaken')}</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('immediateActionTaken')}
              </label>
              <textarea
                value={formData?.immediateActionTaken}
                onChange={(e) => handleChange('immediateActionTaken', e?.target?.value)}
                placeholder={t('immediateActionPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('correctiveMeasures')}
              </label>
              <textarea
                value={formData?.correctiveMeasures}
                onChange={(e) => handleChange('correctiveMeasures', e?.target?.value)}
                placeholder={t('correctiveMeasuresPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <h3 className="text-md font-semibold text-foreground mb-4">{t('attachmentsOptional')}</h3>
          <div className="space-y-4">
            <MobilePhotoInput
              onFilesSelected={(newFiles) => setFiles(prev => [...prev, ...newFiles])}
              uploading={uploading}
              multiple
              hint="Images and PDFs accepted · Max 10MB each"
            />

            {files?.length > 0 && (
              <div className="space-y-2">
                {files?.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <div className="flex items-center gap-2">
                      <Icon name="File" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground truncate max-w-[200px]">{file?.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      iconName="X"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {errors?.submit && (
          <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
            <p className="text-sm text-destructive">{errors?.submit}</p>
          </div>
        )}

        {/* Sticky submit on mobile */}
        <div className="sticky bottom-0 bg-card pt-4 pb-safe border-t border-border -mx-4 px-4 md:static md:border-0 md:pt-4 md:pb-0 md:mx-0 md:px-0">
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history?.back()}
              className="flex-1 sm:flex-none min-h-[48px]"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              loading={uploading}
              iconName="Send"
              iconPosition="left"
              className="flex-1 sm:flex-none min-h-[48px]"
            >
              {t('submitReport')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default IncidentForm;